import React, { useState, useEffect } from 'react';
import { Link } from 'react-scroll';
import { motion, AnimatePresence } from 'framer-motion';
import { FiSun, FiMoon, FiMenu, FiX, FiArrowLeft } from 'react-icons/fi';
import { navLinks, personalInfo } from '../data/portfolioData';
import { useTheme } from '../context/ThemeContext';

const Navbar = ({ isPricing = false }) => {
  const { theme, toggleTheme } = useTheme();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [activeLink, setActiveLink] = useState('');

  const isMobile = window.innerWidth <= 768;

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const closeMobile = () => setMobileOpen(false);

  return (
    <nav
      className={`navbar${scrolled ? ' navbar-scrolled' : ''}`}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100%',
        zIndex: 1000,
        background: 'var(--bg-secondary)',
        borderBottom: '1px solid var(--border-color)',
        boxSizing: 'border-box',
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          maxWidth: '1400px',
          margin: '0 auto',
          padding: '0 1.5rem',
          height: '56px',
        }}
      >
        {/* Logo */}
        <div
          className="navbar-logo"
          style={{ fontFamily: "'Fira Code', 'Courier New', monospace", fontSize: '1.1rem', fontWeight: 600, whiteSpace: 'nowrap' }}
        >
          <span style={{ color: 'var(--accent-color, #569cd6)' }}>&lt;</span>
          <span style={{ color: 'var(--text-primary, #d4d4d4)' }}>
            {isMobile ? personalInfo.shortName : personalInfo.name}
          </span>
          <span style={{ color: 'var(--accent-color, #569cd6)' }}> /&gt;</span>
        </div>

        {/* Desktop Tabs */}
        <div
          className="navbar-tabs"
          style={{
            display: 'flex',
            alignItems: 'center',
            height: '100%',
            gap: 0,
          }}
        >
          {isPricing ? (
            <a
              href="/"
              className="navbar-tab"
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                height: '100%',
                padding: '0 1.1rem',
                cursor: 'pointer',
                fontSize: '0.9rem',
                fontFamily: "'Fira Code', 'Courier New', monospace",
                color: 'var(--text-secondary, #858585)',
                borderTop: '2px solid transparent',
                borderRight: '1px solid var(--border-color)',
                transition: 'color 0.2s, border-color 0.2s',
                userSelect: 'none',
                textDecoration: 'none',
              }}
            >
              <FiArrowLeft /> _back_to_home
            </a>
          ) : (
            navLinks.map((link) => (
              <Link
                key={link.to}
                className={`navbar-tab${activeLink === link.to ? ' active' : ''}`}
                to={link.to}
                spy
                smooth
                offset={-80}
                duration={500}
                onSetActive={() => setActiveLink(link.to)}
                onClick={closeMobile}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  height: '100%',
                  padding: '0 1.1rem',
                  cursor: 'pointer',
                  fontSize: '0.9rem',
                  fontFamily: "'Fira Code', 'Courier New', monospace",
                  color: activeLink === link.to ? 'var(--text-primary, #d4d4d4)' : 'var(--text-secondary, #858585)',
                  borderTop: activeLink === link.to ? '2px solid var(--accent-color, #569cd6)' : '2px solid transparent',
                  borderRight: '1px solid var(--border-color)',
                  transition: 'color 0.2s, border-color 0.2s',
                  userSelect: 'none',
                }}
              >
                {link.name}
              </Link>
            ))
          )}
        </div>

        {/* Actions */}
        <div className="navbar-actions" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <motion.button
            className="theme-toggle"
            onClick={toggleTheme}
            whileTap={{ scale: 0.85 }}
            whileHover={{ rotate: 20 }}
            style={{
              background: 'none',
              border: '1px solid var(--border-color)',
              borderRadius: '6px',
              color: 'var(--text-primary, #d4d4d4)',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '36px',
              height: '36px',
              fontSize: '1.15rem',
            }}
            aria-label="Toggle theme"
          >
            <AnimatePresence mode="wait" initial={false}>
              <motion.span
                key={theme}
                initial={{ opacity: 0, y: -8 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: 8 }}
                transition={{ duration: 0.2 }}
                style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
              >
                {theme === 'dark' ? <FiSun /> : <FiMoon />}
              </motion.span>
            </AnimatePresence>
          </motion.button>

          <button
            className="mobile-menu-btn"
            onClick={() => setMobileOpen((prev) => !prev)}
            style={{
              display: 'none',
              background: 'none',
              border: '1px solid var(--border-color)',
              borderRadius: '6px',
              color: 'var(--text-primary, #d4d4d4)',
              cursor: 'pointer',
              alignItems: 'center',
              justifyContent: 'center',
              width: '36px',
              height: '36px',
              fontSize: '1.25rem',
            }}
            aria-label="Toggle mobile menu"
          >
            {mobileOpen ? <FiX /> : <FiMenu />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            className="mobile-menu"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3, ease: 'easeInOut' }}
            style={{
              overflow: 'hidden',
              background: 'var(--bg-secondary)',
              borderTop: '1px solid var(--border-color)',
            }}
          >
            {isPricing ? (
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
              >
                <a
                  href="/"
                  className="navbar-tab"
                  onClick={closeMobile}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    padding: '0.85rem 1.5rem',
                    cursor: 'pointer',
                    fontSize: '0.95rem',
                    fontFamily: "'Fira Code', 'Courier New', monospace",
                    color: 'var(--text-secondary, #858585)',
                    borderLeft: '2px solid transparent',
                    borderBottom: '1px solid var(--border-color)',
                    transition: 'color 0.2s, border-color 0.2s',
                    textDecoration: 'none',
                  }}
                >
                  <FiArrowLeft /> _back_to_home
                </a>
              </motion.div>
            ) : (
              navLinks.map((link, i) => (
                <motion.div
                  key={link.to}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: i * 0.05 }}
                >
                  <Link
                    className={`navbar-tab${activeLink === link.to ? ' active' : ''}`}
                    to={link.to}
                    spy
                    smooth
                    offset={-80}
                    duration={500}
                    onSetActive={() => setActiveLink(link.to)}
                    onClick={closeMobile}
                    style={{
                      display: 'block',
                      padding: '0.85rem 1.5rem',
                      cursor: 'pointer',
                      fontSize: '0.95rem',
                      fontFamily: "'Fira Code', 'Courier New', monospace",
                      color: activeLink === link.to ? 'var(--text-primary, #d4d4d4)' : 'var(--text-secondary, #858585)',
                      borderLeft: activeLink === link.to ? '2px solid var(--accent-color, #569cd6)' : '2px solid transparent',
                      borderBottom: '1px solid var(--border-color)',
                      transition: 'color 0.2s, border-color 0.2s',
                    }}
                  >
                    {link.name}
                  </Link>
                </motion.div>
              ))
            )}
          </motion.div>
        )}
      </AnimatePresence>


    </nav>
  );
};

export default Navbar;
